/**
 * 旅行心愿 → 联网攻略 → 手账：玩家端的状态换算（TRV-06，claude-6c2b 分身）。
 *
 * 类型全部来自 "@/shared/contracts"（generated.ts 09:37 版 9c5f1b88f2a60008；TRV-00 合同 v1 §23、§28–§31；字段含义见后端 schemas/web/travel.py）：
 * - 心愿：TravelWish / TravelWaitingReason（十二个码）/ TravelWishStatus / TravelResearchStatus；
 * - 计划：TravelPlan（current_revision + revisions，旧版都留着；页面显示 current_revision 那一版）/ TravelPlanRevision /
 *   TravelStop / TravelFact / TravelSource / TravelOwnerTip / TravelJourneySummary；
 * - 手账：在这一版修订的 journals 里（TravelJournal，phase 分 plan 计划页、memory 回忆页；图状态复用 PhotoStatus，外加 redraw_ticket、image_refused）。
 * contracts 里的 TravelGuide / TravelGuideStop（旧攻略）与 TravelActivity / TravellerRole（别的模块）不是这一套，别混用。
 * 本文件只多一个本地组合 PlanBundle（计划 + 它的心愿）。页面（./PlanPage）、列表（../GuideBookPage）、地图那一行（./wishNote）只认 PlanView。
 * live 的计划页（2026-09-24 23:2x 接上 GET /travel/plans/{plan_id}）：心愿（GET /travel/wish）的 wish_id 与计划对得上才放在一起（viewOfPlan）；
 * 对不上（以前的心愿）就只按计划本身说（viewOfPlanOnly）：状态只从计划推（有回忆页＝已回来，关联了行程＝已出发，都没有＝状态待确认），
 * TA 的理由、还差什么、攒钱目标这些只有心愿才有的不显示，也不编。
 *
 * 状态到玩家说法的换算全在这里：玩家只看人话。§5.2 的十二个原因码每个配一句；不认识的码只说中性的话，不露码。
 * 语义不混（§5.2、§11.5）：missing_funds ≠ quota_denied（平台的次数）；research_unknown ≠ 搜不到；fact_stale ≠ 地名不存在；
 * 手账图 unknown ≠ failed（unknown 可能已经画了并计费，只说“还没确认”）；重画入口只在 failed / unknown 且后端给了 redraw_ticket 时出现。
 * 核验：结论只看 verdict；verification 是核验方法，conclusion 是端口自己的说法（只记录、不照单全收），页面都不显示。
 * 三种钱分开：攒钱目标（funds_goal、target_coins，游戏星币）不是路费；路费只认计划这一版上的行程摘要
 * （TravelJourneySummary：fare 是标价、用没用券都不变，fare_waived 为真＝用了驾校借车券、实付 0，§30；
 *  用券时写“用了驾校借车券，这趟不用租车费”，标价划掉显示，绝不把标价说成付过的钱）；
 *  平台补助（fare_sponsor 非空，先看它，再看 fare_waived，最后看 fare）：写“这趟路费由星球活动补助”“TA 没有花钱”，标价照样划掉；
 *  补助来源（{kind}:{ref}）的码和活动号不给玩家看，不认识的种类也按补助说（“这趟路费由星球补助”）；
 * 现实参考费用挂在价钱类事实上（value 是 {amount, currency, estimated}）：只报已核实、三样都认得出、有日期的，标币种、日期、是否估算；
 * 没核实写“还没核实”，认不出来（包括没写 estimated）整条不报金额，都不写 0；平台调用费用（研究收据）玩家页面永远不读。
 * 计划和回忆分开：出发前不盖到访章；顺路建议单独标出、不算到访；到访只认这一版计划的回忆手账站点上的真实事件
 * （TravelStop.visited_event_ids 计划阶段恒为空、回忆页才有；跨版本不认），回来后没去成的写“下次”。
 * 地图外链只用后端给的 nav_url（高德、WGS-84、核实过的地点才有），前端不拿 lat/lng 自己拼。
 * 联网检索（能力表 travel.research，2026-09-26 I 加）不是 available 时（没开通、还没读到能力表；演示按能查说）：资料一时查不到、也核不了，
 * 页面不许诺会去查、会补上、还在确认——“资料准备中 / 晚点查 / 待确认 / 待补 / 待更新 / 待核 / 查好资料以后 / 重新查一遍”都换成眼下的实情
 * （见 WAITING_REASON_CHIPS_NO_RESEARCH、waitingItem 里的 ctx.research 分支、PLAN_NOTES 的两套）；和研究无关的（钱、天气、约定、维护、手账图）照旧。
 */
import {
  TravelWaitingReasonValues,
  type TravelFact,
  type TravelJournal,
  type TravelJourneySummary,
  type TravelOwnerTip,
  type TravelPlan,
  type TravelPlanRevision,
  type TravelSafetySticker,
  type TravelSource,
  type TravelStop,
  type TravelStopRole,
  type TravelWaitingReason,
  type TravelWish,
  type PlanetEventTag,
} from "@/shared/contracts";
import type { ChipTone, IconName } from "@/shared/ui";

/**
 * 计划页的读模型（本地组合，不是 DTO）：一份计划 + 它的心愿。I 定：
 * - 心愿的业务状态与 TA 的理由从 GET /travel/wish 读（TravelWish.status、owner_reason）；
 * - 计划从 GET /travel/plans/{plan_id} 读，手账在这一版修订的 journals 里。
 * 第③期（live）只在两边的 wish_id 对得上时把它们放在一起；对不上（比如以前的心愿）不猜状态，只按计划本身说（viewOfPlanOnly）。
 */
export interface PlanBundle {
  wish: TravelWish;
  plan: TravelPlan;
}

/* ======================= 视图模型：页面只认这个 ======================= */

/** 游戏币的叫法：与钱包、出发站、攻略页一致（主窗口定：用“星币”）。 */
export const COIN_UNIT = "星币";

/**
 * 数字和单位之间的不断行空格（U+00A0；2026-09-25 巡检）：窄屏折行时“45”不和“星币”分家、不单独掉到下一行。
 * 源码里写成可见的转义，别换成看不见的字符。
 */
export const NBSP = "\u00A0";

/** “45 星币”：数字和星币连在一起。 */
const coinsText = (n: number | string) => `${n}${NBSP}${COIN_UNIT}`;

/** 页面分支用；玩家看不到这几个词。 */
export type PlanStage = "wish" | "ready" | "departed" | "back" | "cancelled" | "unknown";

export interface WaitingItem {
  key: string;
  icon: IconName;
  text: string;
  detail: string | null;
}

export interface PlaceStamp {
  kind: "visited" | "next";
  label: string;
}

export interface PlaceView {
  /**
   * 同一版之内的键：plan_revision + station_id（计划阶段 station_id 可空，空时用序号）。
   * station_id 只在一版计划内有效；跨版本对应同一个地点用 name + role（I 定稿）。
   */
  id: string;
  role: TravelStopRole;
  name: string;
  why: string | null;
  tip: string | null;
  /** 这个地点依据的资料里有过期、说法不一、没核实或找不到的。 */
  pending: boolean;
  /** 后端的 TravelStop.verified。 */
  verified: boolean;
  /** “地点已核对 / 地点暂时核不了 / 地点待确认”；本地去处（附近走走的地方）本来就不需要核实，为 null——地点卡不写这一行。 */
  verifyText: string | null;
  /** 只有这一版计划的回忆手账里的真实事件才有“到过”；回来后没去成的是“下次”；出发前一律没有。 */
  stamp: PlaceStamp | null;
  /** 后端给的 nav_url，只收 http(s)；没有就不给地图链接（不拿坐标自己拼）。 */
  mapUrl: string | null;
}

export interface LineView {
  text: string;
  /** 支持它的资料里有过期、说法不一、没核实或找不到的，标“待确认”。 */
  pending: boolean;
}

/** 公告的一个来源：有网址才带链接（只收 http(s)），没有网址只写名字。 */
export interface NoticeSourceView {
  publisher: string;
  href: string | null;
}

/** 一条公告（notice 类事实）：原话、老实的一句（没核实 / 过期 / 说法不一时）、它自己的来源（一条都对不上时为空，这一行不写）。 */
export interface NoticeView {
  factId: string;
  text: string;
  note: string | null;
  sources: NoticeSourceView[];
}

/**
 * 贴纸类安全提醒（2026-09-27 用户定：安全类公告做成可爱的东西，TA 用自己的口吻提醒）：这一版计划的提醒里，
 * 后端标了认得的贴纸（TravelOwnerTip.safety_sticker，I / A 定：A 按这条提醒引用的公告用封闭关键词表判，不信模型自报）的那几条，
 * 连同它依据的每条公告的原话、标注与来源。
 * 视觉等参考图（主窗口 2026-09-27 暂停便签样式），页面眼下仍把它们和别的提醒一起放在“提醒”列表里（reminders 里照旧有），
 * 只在那一行圆点的位置贴上贴纸图（有图才贴）；index 是它在 reminders 里的位置，以后画便签时据此把它从普通列表里拿出来。
 */
export interface NoticeTipView {
  index: number;
  text: string;
  /** 贴哪张贴纸（SAFETY_STICKERS 的键）。 */
  sticker: TravelSafetySticker;
  notices: NoticeView[];
}

/**
 * 贴纸表：契约的 TravelSafetySticker 每个值一张（按 Record 穷举，契约加值漏配就编译不过）。
 * 图是 r7k 的素材（shark_flag：UI-ASSET-015 v1 手绘矢量，计划页和手账共用这一张）；image 为 null 时页面不画贴纸图；name 是读屏和图片说明用的叫法。
 */
export const SAFETY_STICKERS: Record<TravelSafetySticker, { name: string; image: string | null }> = {
  shark_flag: { name: "鲨鱼旗", image: "/ui-assets/UI-ASSET-015/v1/safety-shark-flag.svg" },
};

/** 后端给的贴纸键：认得的才算；null、没有这个字段、认不出的值都当没有（不画、不露码）。 */
export function safetyStickerOf(value: unknown): TravelSafetySticker | null {
  return typeof value === "string" && Object.prototype.hasOwnProperty.call(SAFETY_STICKERS, value) ? (value as TravelSafetySticker) : null;
}

export interface CoinView {
  text: string;
  detail: string | null;
  /** 用了驾校借车券、或者路费由平台补助时这趟的标价（页面划掉显示）；其余情况为 null。它是标价，不是付过的钱。 */
  listPrice: string | null;
}

export interface RealCostView {
  key: string;
  text: string;
  meta: string;
}

/** 出发前会再核一遍的资料（TravelPlanRevision.preconditions：出发时必须仍然有效的关键事实）。 */
export interface PreconditionView {
  key: string;
  topic: string;
  detail: string | null;
  /** 不是已核对的（或这条资料找不到了）：待确认的说法。 */
  pendingText: string | null;
  /** 已核对且带有效期的：有效到哪天。 */
  until: string | null;
}

export interface JournalStationView {
  key: string;
  name: string;
  role: TravelStopRole;
  /** 只有回忆页有章：到过 / 下次。 */
  stamp: PlaceStamp | null;
}

/** 手账这一页上写的文字（图外可读，TRV-07：屏幕先显示图外文字）。 */
export interface JournalTextView {
  title: string | null;
  summary: string | null;
  stations: JournalStationView[];
  tips: LineView[];
  /** 手账提醒里的贴纸类安全提醒：口径与计划页 PlanView.noticeTips 相同，index 是它在 tips 里的位置。 */
  noticeTips: NoticeTipView[];
  rain: string | null;
}

export interface JournalView {
  /**
   * text_only＝这一页按设计不画图（本地心愿的最简计划，image_refused 是 local_trip_no_image）：只有文字，没有图框，
   * 不说“只有文字 / 没有配图 / 没画成 / 还没确认”，不给重画。none＝没接插画（老数据，图字段全 null、没有这个码）。
   */
  state: "ready" | "drawing" | "unknown" | "failed" | "refused" | "none" | "text_only";
  heading: string;
  caption: string;
  imageUrl: string | null;
  /** 画面按上一版计划画的时候提醒一句（只在有图时）。 */
  staleNote: string | null;
  /** 重画入口：只在 failed / unknown 且后端给了 redraw_ticket 时出现（主人自己点才重画，不自动重画）。 */
  canRedraw: boolean;
  /** 重画要照抄的那一页（canRedraw 时才有）：journal_id 与后端给的 redraw_ticket 原样（请求体里 expected_revision 是这一版的 plan_revision）。 */
  redraw: { journalId: string; ticket: string } | null;
  /** 画里的 TA 从哪来（identity_mode 说人话）；只在有图或正在画时说。 */
  identity: string | null;
  /** 手账这一页的文字；没有手账时为 null。 */
  text: JournalTextView | null;
  /** 这一页自己的时间（TravelJournal.created_at，不拿计划的时间顶）：“这一页写于 …”；没有手账或认不出来时为 null。 */
  time: string | null;
  /** 这一趟车费由星球活动补助时的活动（TravelJournal.planet_event，看板 #11）；没有、或活动名是空的为 null，页面上不编名字。 */
  planetEvent: PlanetEventTag | null;
}

export interface SourceRefView {
  key: string;
  publisher: string;
  href: string | null;
  times: string[];
}

export interface SourceView {
  id: string;
  topic: string;
  /** 这条资料说的是什么（过期、说法不一的旁边有“待确认”，不冒充现在有效）。 */
  detail: string | null;
  pendingText: string | null;
  refs: SourceRefView[];
  /** 资料本身的观测 / 适用 / 发布时间；都没有时照实写“资料本身没有注明日期”。 */
  times: string[];
}

/** 几处固定的说法：随联网检索（travel.research）能不能用而变——不能用时不许诺会去查、会补上、还在确认。 */
export interface PlanNotes {
  /** “出发前会再确认”那一块的说明。 */
  preconditions: string;
  /** 还没有计划时，“行动安排”“顺路建议”“资料来源”三块各一句。 */
  noSteps: string;
  noAlong: string;
  noSources: string;
  /** 依据的资料有问题时：地点卡旁、提醒旁的小标签。 */
  placePending: string;
  linePending: string;
}

export interface PlanView {
  /** 列表里的键：有计划用 plan_id，没有计划用 wish_id。 */
  key: string;
  /** 点进去的地址：当前心愿 /guides/wish，某一份计划 /guides/plan/:planId（§23.4）。 */
  href: string;
  planRevision: number | null;
  isDemo: boolean;
  stage: PlanStage;
  title: string;
  destination: string;
  /** 这一版计划的标题（TravelPlanRevision.title）。 */
  planTitle: string | null;
  status: { label: string; tone: ChipTone };
  /** 页头一句话。 */
  summary: string;
  /** 列表卡片上的一句：想去 / 准备中时是“还差什么”的第一条。 */
  cardLine: string;
  reason: string | null;
  waiting: WaitingItem[];
  /** 出发前会再核一遍的资料（只在想去 / 可以出发时有）。 */
  preconditions: PreconditionView[];
  /** 行动安排：计划这一版里 TA 写的那段（summary）。 */
  arrangement: string | null;
  planWritten: boolean;
  /**
   * 心愿已经有写好的计划（plan_id 和 plan_revision 都有，见 publishedPlanOf）、但这一页没读到计划全文时，去那份计划的地址。
   * 只有 plan_id 不算：资料还在查、资料待补的心愿也有 plan_id，点进去是“这份计划还没写好”（DS5）。
   */
  planLink: string | null;
  validity: string | null;
  main: PlaceView;
  suggestions: PlaceView[];
  reminders: LineView[];
  /** 提醒里后端标了贴纸的那几条（贴纸类安全提醒的数据；页面眼下不单独画，见 NoticeTipView）。 */
  noticeTips: NoticeTipView[];
  rainPlan: string | null;
  coins: CoinView | null;
  realCosts: RealCostView[];
  journal: JournalView;
  sources: SourceView[];
  notes: PlanNotes;
}

export interface ViewOptions {
  /** 当前宠物的名字；拿不到就写“TA”（主窗口定）。 */
  petName?: string | null;
  /** 演示数据：页面处处挂“演示”，现实参考金额后面带“（演示）”。DTO 里没有这个字段，由读数据的一侧告诉。 */
  isDemo?: boolean;
  /** TA 所在城市的时区。DTO 里没有，从哪来待用户决定；没给就用暂定的 PROVISIONAL_ZONE。 */
  timezone?: string | null;
  /**
   * 能力表里 travel.research（联网攻略）是不是 available，由读数据的一侧告诉（./research 的 useResearchAvailable，读法同 ./JournalRedraw）。
   * 不是 available 时资料一时查不到、也核不了：页面不许诺会去查、会补上、还在确认。没给按不能查说（宁可不许诺）。
   */
  researchAvailable?: boolean;
}

/* ======================= 换算 ======================= */

const STAGE_OF: Record<string, PlanStage> = { active: "wish", ready: "ready", linked: "departed", completed: "back", cancelled: "cancelled" };

function isReason(code: string): code is TravelWaitingReason {
  return (TravelWaitingReasonValues as readonly string[]).includes(code);
}

/** “还差什么”的先后：维护 → 资料 → 计划 → 钱 → 天气 → 约定；第一条同时决定状态标签、列表卡片那句和地图那一行。 */
const WAITING_ORDER: Record<TravelWaitingReason, number> = {
  maintenance: 0,
  research_pending: 1,
  quota_denied: 2,
  research_unknown: 3,
  research_failed: 4,
  plan_stale: 5,
  fact_stale: 6,
  fact_unverified: 7,
  fact_conflicting: 8,
  missing_funds: 9,
  weather_unsuitable: 10,
  commitment_active: 11,
};

/** §5.2 十二个码的状态标签（类型上必须覆盖全部；用例另外手抄合同清单双向核对）。 */
export const WAITING_REASON_CHIPS: Record<TravelWaitingReason, { label: string; tone: ChipTone }> = {
  maintenance: { label: "维护中", tone: "neutral" },
  research_pending: { label: "资料准备中", tone: "sky" },
  quota_denied: { label: "资料晚点查", tone: "sky" },
  research_unknown: { label: "资料待确认", tone: "sun" },
  research_failed: { label: "资料待补", tone: "coral" },
  plan_stale: { label: "计划待更新", tone: "sun" },
  fact_stale: { label: "资料待确认", tone: "sun" },
  fact_unverified: { label: "资料待确认", tone: "sun" },
  fact_conflicting: { label: "资料待确认", tone: "sun" },
  missing_funds: { label: `等攒够${COIN_UNIT}`, tone: "sun" },
  weather_unsuitable: { label: "等天气", tone: "sky" },
  commitment_active: { label: "有约在先", tone: "neutral" },
};

/**
 * 联网检索不能用时（travel.research 不是 available）的状态标签：资料一时查不到、也核不了，这几种状态可能一直等不来——
 * 不说“准备中 / 晚点查 / 待确认 / 待补 / 待更新”，只说眼下的实情，也不用“在进行”的颜色（sky）。
 * 研究侧四个码（还没查、次数被拒、结果没确认、没查成）对主人来说是同一件事：资料暂时查不到；各自的实情在“还差什么”里分开说。
 * 和研究无关的四个码（维护、钱、天气、约定）照旧。
 */
export const WAITING_REASON_CHIPS_NO_RESEARCH: Record<TravelWaitingReason, { label: string; tone: ChipTone }> = {
  ...WAITING_REASON_CHIPS,
  research_pending: { label: "资料暂时查不到", tone: "neutral" },
  quota_denied: { label: "资料暂时查不到", tone: "neutral" },
  research_unknown: { label: "资料暂时查不到", tone: "neutral" },
  research_failed: { label: "资料暂时查不到", tone: "neutral" },
  plan_stale: { label: "计划暂时更新不了", tone: "sun" },
  fact_stale: { label: "资料暂时核不了", tone: "sun" },
  fact_unverified: { label: "资料暂时核不了", tone: "sun" },
  fact_conflicting: { label: "资料暂时核不了", tone: "sun" },
};

const STAGE_CHIP: Record<Exclude<PlanStage, "wish">, { label: string; tone: ChipTone }> = {
  ready: { label: "可以出发", tone: "leaf" },
  departed: { label: "已出发", tone: "sky" },
  back: { label: "已回来", tone: "leaf" },
  cancelled: { label: "已取消", tone: "neutral" },
  unknown: { label: "状态待确认", tone: "neutral" },
};

/** 事实类别的叫法（开放集合；2026-09-25 定稿，见 generated 的 TravelFact.category 说明）；不认识的写“其他资料”。 */
const CATEGORY_TEXT: Record<string, string> = {
  destination_identity: "地点",
  route: "怎么去",
  opening: "开放时间",
  weather: "天气",
  notice: "公告",
  ticket_price: "门票",
  transport_price: "交通费",
  feature: "看点",
  landmark: "地标",
};

/** 主窗口定的六种中文名；其余照三字母代码显示。 */
const CURRENCY_TEXT: Record<string, string> = { CNY: "人民币", HKD: "港币", MOP: "澳门元", JPY: "日元", USD: "美元", TWD: "新台币" };

/** 结论（verdict）的说法。rejected＝引用了不存在的来源、上游产出有问题，与 unverified（查不到可用来源）不折叠。 */
const VERDICT_TEXT: Record<string, string> = { stale: "过期了，待确认", conflicting: "说法不一，待确认", unverified: "还没核实，待确认", rejected: "没有采用" };
/** 联网检索不能用时：过期、说法不一、没核实的资料一时核不了（不写“待确认”）；没有采用的照旧。 */
const VERDICT_TEXT_NO_RESEARCH: Record<string, string> = { stale: "过期了，暂时核不了", conflicting: "说法不一，暂时核不了", unverified: "还没核实，暂时核不了", rejected: "没有采用" };

/** 几处固定说法的两套（见 PlanNotes）：能查时照原样；不能查时不许诺“查好以后会写 / 正在准备 / 重新查一遍 / 待确认”。 */
export const PLAN_NOTES: PlanNotes = {
  preconditions: "出发那一刻，TA 会再确认这几条资料还有效；过期了就先不出发，重新查一遍。",
  noSteps: "TA 查好资料以后，路线和提醒会写在这里。",
  noAlong: "TA 查好资料以后，再看有没有顺路的地方。",
  noSources: "还没有资料来源：TA 正在准备。",
  placePending: "资料待确认",
  linePending: "待确认",
};
export const PLAN_NOTES_NO_RESEARCH: PlanNotes = {
  // 出发那一刻的核对只看资料的有效期，不用联网，照样会做；“重新查一遍”兑现不了，不说。
  preconditions: "出发那一刻，TA 会再确认这几条资料还有效；过期了就先不出发。",
  noSteps: "资料暂时查不到，还没有路线和提醒。",
  noAlong: "资料暂时查不到，还没有顺路建议。",
  noSources: "资料暂时查不到，还没有资料来源。",
  placePending: "资料暂时核不了",
  linePending: "暂时核不了",
};

/**
 * 手账里 TA 的样子从哪来（TravelIdentityMode：用证件照，还是不画 TA）。不认识的取值不说（不露码）。
 * 不画 TA 的原因在 identity_note 里（是原因码，比如没有可用的参考照片），不上页面（X-09）。
 */
const IDENTITY_TEXT: Record<string, string> = { photo: "画里的 TA 照着 TA 的证件照画", none: "这页手账不画 TA 的样子" };

/**
 * 暂定时区：Asia/Shanghai。
 * TA 所在城市的时区从哪来（DTO 里没有）是待用户决定的事项。这里是“暂定”，不是“默认”：决定之后改这里，别当成已经定了的规则。
 */
export const PROVISIONAL_ZONE = "Asia/Shanghai";

function categoryText(code: string): string {
  return CATEGORY_TEXT[code] ?? "其他资料";
}

function currencyText(code: string): string {
  return CURRENCY_TEXT[code] ?? code;
}

function zoneOf(tz: string | null | undefined): string {
  if (!tz) return PROVISIONAL_ZONE;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return tz;
  } catch {
    return PROVISIONAL_ZONE;
  }
}

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

function partsAt(ms: number, zone: string, withTime: boolean): Record<string, string> {
  const options: Intl.DateTimeFormatOptions = { timeZone: zone, year: "numeric", month: "numeric", day: "numeric" };
  if (withTime) Object.assign(options, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
  const out: Record<string, string> = {};
  for (const part of new Intl.DateTimeFormat("en-US", options).formatToParts(new Date(ms))) out[part.type] = part.value;
  return out;
}

/** “2026年9月24日”。纯日期不做时区换算；时间点按 TA 所在城市的时区取日期。认不出来就不显示（不伪造）。 */
export function formatDay(value: string | null, zone: string): string | null {
  if (!value) return null;
  const m = DATE_ONLY.exec(value);
  if (m) return `${Number(m[1])}年${Number(m[2])}月${Number(m[3])}日`;
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return null;
  const p = partsAt(ms, zone, false);
  return `${p.year}年${p.month}月${p.day}日`;
}

/** 有效期的右端（§29.3 左闭右开 [valid_from, valid_until)）：最后有效的是 valid_until 前一刻，所以按前一刻取日期。纯日期照写。 */
function formatUntilDay(value: string | null, zone: string): string | null {
  if (!value) return null;
  if (DATE_ONLY.test(value)) return formatDay(value, zone);
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return null;
  const p = partsAt(ms - 1, zone, false);
  return `${p.year}年${p.month}月${p.day}日`;
}

/** “2026年9月24日 05:40”；纯日期只到日。 */
export function formatMoment(value: string | null, zone: string): string | null {
  if (!value) return null;
  if (DATE_ONLY.test(value)) return formatDay(value, zone);
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return null;
  const p = partsAt(ms, zone, true);
  return `${p.year}年${p.month}月${p.day}日 ${p.hour}:${p.minute}`;
}

/** 发布时间：落在当地零点的只写日期（发布日多半只到日，写“00:00”像是编出来的），不在零点的写到分钟。 */
function formatPublished(value: string | null, zone: string): string | null {
  if (!value) return null;
  if (DATE_ONLY.test(value)) return formatDay(value, zone);
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return null;
  const p = partsAt(ms, zone, true);
  return p.hour === "00" && p.minute === "00" ? `${p.year}年${p.month}月${p.day}日` : `${p.year}年${p.month}月${p.day}日 ${p.hour}:${p.minute}`;
}

function rangeText(from: string | null, until: string | null, zone: string): string | null {
  const a = formatDay(from, zone);
  const b = formatUntilDay(until, zone);
  if (a && b) return a === b ? a : `${a} 至 ${b}`;
  if (a) return `${a} 起`;
  if (b) return `到 ${b} 为止`;
  return null;
}

/** 外链只收 http(s)；javascript:、data:、ftp: 之类一律不做成链接。 */
export function safeHref(raw: string | null | undefined): string | null {
  if (!raw) return null;
  try {
    const url = new URL(raw);
    return url.protocol === "https:" || url.protocol === "http:" ? url.href : null;
  } catch {
    return null;
  }
}

function hostOf(raw: string | null): string | null {
  const href = safeHref(raw);
  return href ? new URL(href).hostname : null;
}

/** 手账图只收 http(s) 或站内路径（/media/...）；不收协议相对地址和其他协议。 */
function safeImageUrl(raw: string | null): string | null {
  if (!raw) return null;
  if (raw.startsWith("/") && !raw.startsWith("//")) return raw;
  return safeHref(raw);
}

function amountText(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
}

/**
 * 价钱类事实的 value：{ amount, currency, estimated }（后端定）。三样都认得出才算数，否则整条不报金额：
 * - 金额要是非负数，币种要是 ISO 4217 三字母大写；
 * - estimated 必须写明（true / false）。A 的口径（I 转）：不写 estimated 等于认不出来——
 *   不知道是不是估算的价格，比没有价格更坏，它会让人按准确价格去规划；猜一个默认值，就是替用户做了一个他不知道的假设。
 */
function priceOf(value: unknown): { amount: number; currency: string; estimated: boolean } | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const { amount, currency, estimated } = value as Record<string, unknown>;
  if (typeof amount !== "number" || !Number.isFinite(amount) || amount < 0) return null;
  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) return null;
  if (typeof estimated !== "boolean") return null;
  return { amount, currency, estimated };
}

/** 价钱类（定稿）：门票、交通费，值是 {amount, currency, estimated}。 */
const PRICE_CATEGORIES = new Set(["ticket_price", "transport_price"]);
const isPriceFact = (fact: TravelFact) => PRICE_CATEGORIES.has(fact.category);

const isVerified = (fact: TravelFact | undefined) => fact?.verdict === "verified";

interface Ctx {
  stage: PlanStage;
  zone: string;
  isDemo: boolean;
  /** 这份计划的心愿；live 读到的当前心愿与计划对不上时为 null（只按计划本身说）。 */
  wish: TravelWish | null;
  revision: TravelPlanRevision | null;
  facts: Map<string, TravelFact>;
  factList: TravelFact[];
  sources: Map<string, TravelSource>;
  /** 这一版计划的回忆手账里的站点（到访只认它）；没有就是 null。 */
  memory: TravelStop[] | null;
  /** 联网检索能不能用（ViewOptions.researchAvailable 为真才算）；不能用时不许诺会去查、会补上、还在确认。 */
  research: boolean;
}

/** 资料有问题时那半句：能查时“还在确认”，不能查时“暂时核不了”（不许诺）。 */
const checking = (ctx: Ctx) => (ctx.research ? "还在确认" : "暂时核不了");

/** 地点没核对时那一句：能查时“地点待确认”，不能查时“地点暂时核不了”。 */
const unverifiedPlace = (ctx: Ctx) => (ctx.research ? "地点待确认" : "地点暂时核不了");

/** 一条事实没核对时旁边的小标签（资料来源、出发前会再确认两处共用）；已核对的没有。 */
function pendingTextOf(fact: TravelFact, ctx: Ctx): string | null {
  if (isVerified(fact)) return null;
  return ctx.research ? (VERDICT_TEXT[fact.verdict] ?? "待确认") : (VERDICT_TEXT_NO_RESEARCH[fact.verdict] ?? "暂时核不了");
}

/** 某个结论的资料说的是哪几类；优先列会卡住出行的（blocks_departure），一条都没卡住才列全部。 */
function topicsWith(ctx: Ctx, verdicts: string[]): string | null {
  const hits = ctx.factList.filter((f) => verdicts.includes(f.verdict));
  const blocking = hits.filter((f) => f.blocks_departure);
  const topics = [...new Set((blocking.length ? blocking : hits).map((f) => categoryText(f.category)))];
  return topics.length ? topics.join("、") : null;
}

function waitingItem(code: string, index: number, wish: TravelWish, ctx: Ctx): WaitingItem {
  const key = `${index}-${code}`;
  switch (code) {
    case "missing_funds": {
      const { target_coins, current_coins, funds_goal, last_considered_at } = wish;
      const target = target_coins ?? funds_goal;
      if (target == null || current_coins == null) return { key, icon: "coin", text: `${COIN_UNIT}还没攒够`, detail: target != null ? `要攒到 ${coinsText(target)}` : null };
      const short = Math.max(0, target - current_coins);
      // 钱包是快照，快照时刻就是 last_considered_at（I 答）。这是攒钱的目标，不是路费。
      const at = formatMoment(last_considered_at, ctx.zone);
      return {
        key,
        icon: "coin",
        text: short > 0 ? `还差 ${coinsText(short)}` : `${COIN_UNIT}还在核对`,
        // “有 45 星币”：带上单位，数字和单位之间不断行（原来“有 45”在窄屏上会把 45 单独甩到下一行）。
        detail: `要攒到 ${coinsText(target)}；${at ? `截至 ${at} ` : ""}有 ${coinsText(current_coins)}`,
      };
    }
    // 联网检索不能用时（ctx.research 为假），下面这几项可能一直等不来：不说“正在查 / 还在准备 / 改天再查 / 要重新排 / 还在确认”，
    // 只说眼下的实情；图标也不用转圈（refresh）、指南针（compass）这种“在进行”的样子。
    case "quota_denied":
      // 不能查时“改天再查”兑现不了：和还没查一样，只说资料暂时查不到（也不说星币，这不是 TA 的钱）。
      if (!ctx.research) return { key, icon: "info", text: "资料暂时查不到", detail: null };
      return { key, icon: "refresh", text: "今天查资料的次数用完了，改天再查", detail: null };
    case "research_pending":
      if (!ctx.research) return { key, icon: "info", text: "资料暂时查不到", detail: null };
      return { key, icon: "compass", text: wish.research_state === "running" ? "TA 正在查资料" : "资料还在准备", detail: null };
    case "research_unknown":
      // 上一次的结果还没确认是实情（可能已经发出），能不能查都这么说；不说“搜不到”。
      return { key, icon: ctx.research ? "refresh" : "info", text: "查资料的结果还没确认", detail: null };
    case "research_failed":
      return { key, icon: "info", text: "这次查资料没有完成", detail: null };
    case "plan_stale":
      return ctx.research
        ? { key, icon: "refresh", text: "计划是按之前的想法排的，要重新排一下", detail: null }
        : { key, icon: "info", text: "计划是按之前的想法排的，暂时没法重新排", detail: null };
    case "fact_stale": {
      const topics = topicsWith(ctx, ["stale"]);
      return { key, icon: ctx.research ? "refresh" : "info", text: `有资料过期了，${checking(ctx)}`, detail: topics ? `过期的是：${topics}` : null };
    }
    case "fact_unverified": {
      const topics = topicsWith(ctx, ["unverified", "rejected"]);
      return { key, icon: "info", text: `有关键资料还没核实，${checking(ctx)}`, detail: topics ? `还没核实的是：${topics}` : null };
    }
    case "fact_conflicting": {
      const topics = topicsWith(ctx, ["conflicting"]);
      return { key, icon: "info", text: `有资料说法不一，${checking(ctx)}`, detail: topics ? `说法不一的是：${topics}` : null };
    }
    case "weather_unsuitable":
      return { key, icon: "sparkle", text: "天气不太合适，等一等", detail: null };
    case "commitment_active":
      // §16.2：只用中性措辞，不带任何私聊原话。
      return { key, icon: "user", text: "家里有安排，先不出门", detail: null };
    case "maintenance":
      return { key, icon: "settings", text: "星球这边在维护，出门先等一等", detail: null };
    default:
      return { key, icon: "info", text: "还有一件事在确认", detail: null };
  }
}

const afterDeparture = (stage: PlanStage) => stage === "departed" || stage === "back" || stage === "cancelled";

/** 回忆手账里有没有这一站的真实到访：同一版之内按 name + role 对应（计划阶段 station_id 可空）；两边都有 station_id 时还要相同。 */
function visitedIn(stations: TravelStop[], stop: TravelStop): boolean {
  return stations.some(
    (s) => s.name === stop.name && s.role === stop.role && (s.station_id == null || stop.station_id == null || s.station_id === stop.station_id) && s.visited_event_ids.length > 0,
  );
}

function placeView(stop: TravelStop, index: number, revision: TravelPlanRevision, ctx: Ctx): PlaceView {
  const own = stop.fact_ids.map((id) => ctx.facts.get(id));
  // 盖章只认这一版计划的回忆手账里的真实事件，而且只在出发之后：出发前（想去 / 准备去 / 状态不明）即使数据里混进了事件也不盖。
  let stamp: PlaceStamp | null = null;
  if (afterDeparture(ctx.stage) && ctx.memory) {
    if (visitedIn(ctx.memory, stop)) stamp = { kind: "visited", label: "到过" };
    else if (ctx.stage === "back") stamp = { kind: "next", label: "下次" };
  }
  return {
    id: `${revision.plan_revision}:${stop.station_id ?? `#${index}`}`,
    role: stop.role,
    name: stop.name,
    why: stop.why?.trim() || null,
    tip: stop.tip?.trim() || null,
    // 找不到的事实 ID 也算待确认：不存在的来源不能通过（§11.1）。
    pending: own.some((f) => !isVerified(f)),
    verified: stop.verified,
    verifyText: isLocalPlan(revision) ? null : stop.verified ? "地点已核对" : unverifiedPlace(ctx),
    stamp,
    mapUrl: safeHref(stop.nav_url),
  };
}

/**
 * 提醒（2026-09-25 定稿，见 generated 的 TravelOwnerTip；合同 §18.4）：带数字的由已核验事实支持；
 * fact_ids 为空＝一般叮嘱（不带数字，比如“记得带水”），照常显示、不挂来源、不标待确认。
 * 带 fact_ids 的，支持它的资料里有过期、说法不一、没核实或找不到的，标“待确认”。
 */
function reminderViews(tips: TravelOwnerTip[], ctx: Ctx): LineView[] {
  return tips.filter((tip) => tip.text.trim()).map((tip) => ({ text: tip.text.trim(), pending: tip.fact_ids.some((id) => !isVerified(ctx.facts.get(id))) }));
}

/**
 * 公告没核实、过期、说法不一时跟在公告原话后面的一句（2026-09-27 主窗口定：老实、不吓人）：说清资料的实情，再说 TA 照样按提醒做；
 * 不说“待确认”“会去查”，联网检索能不能用都是这一句。核实过的没有这一句；认不出的结论照“还没核实”说。
 * 被否掉的（rejected）不走到这里：原话不显示（见 noticeTipViews）。
 */
export function noticeNote(verdict: string, who: string): string | null {
  switch (verdict) {
    case "verified":
      return null;
    case "unverified":
      return `这条公告没查到日期，${who} 还是照着做。`;
    case "stale":
      return `这条公告可能不是最新的，${who} 还是照着做。`;
    case "conflicting":
      return `这条公告和别处的说法不一样，${who} 按稳妥的来。`;
    default:
      return `这条公告还没核实，${who} 还是照着做。`;
  }
}

/**
 * 一条公告的来源（主窗口定）：对得上的来源逐个列——有网址带链接（只收 http(s)，别的协议只留名字），没有网址只写名字；
 * 名字空着就用网址的主机名，两样都没有的不列；同名同址的只列一次。一个都对不上（未核实的公告，来源可能整条缺）就是空的，页面不写“来源”那一行。
 */
function noticeSources(fact: TravelFact, ctx: Ctx): NoticeSourceView[] {
  const seen = new Set<string>();
  return fact.source_ids.flatMap((id) => {
    const src = ctx.sources.get(id);
    if (!src) return [];
    const href = safeHref(src.url);
    const publisher = src.publisher?.trim() || hostOf(src.url);
    if (!publisher) return [];
    const key = `${publisher}|${href ?? ""}`;
    if (seen.has(key)) return [];
    seen.add(key);
    return [{ publisher, href }];
  });
}

/**
 * 贴纸类安全提醒的数据（口径 I、A 定，2026-09-27 契约 TravelOwnerTip.safety_sticker）：后端标了认得的贴纸（safetyStickerOf）的提醒才算；
 * 是不是公告、说的是不是鲨鱼，由 A 发布计划时判，前端不另外猜（光是依据里有公告不算——护照、码头维修这类也是公告）。
 * A 的判法落之前这个字段恒为 null，这里就是空的。
 * 这条提醒依据的公告全都列出来（安全事项不能藏），每条带自己的原话、标注和来源。
 * 被否掉的公告（rejected）和原话为空的不列——不能把被否掉的内容写出来。一条提醒依据的公告全被这样去掉时，就算标了贴纸也不画：
 * 便签要能给人看公告原话、证明这是真的安全事项，一条原话都拿不出来就只照普通提醒显示（TA 的那句照常在“提醒”列表里，不藏）。
 */
function noticeTipViews(tips: TravelOwnerTip[], ctx: Ctx, who: string): NoticeTipView[] {
  return tips
    .filter((tip) => tip.text.trim())
    .flatMap((tip, index) => {
      const sticker = safetyStickerOf(tip.safety_sticker);
      if (!sticker) return [];
      const notices = [...new Set(tip.fact_ids)].flatMap((id) => {
        const fact = ctx.facts.get(id);
        if (!fact || fact.category !== "notice") return [];
        const text = factValueText(fact, ctx);
        return text ? [{ factId: fact.fact_id, text, note: noticeNote(fact.verdict, who), sources: noticeSources(fact, ctx) }] : [];
      });
      return notices.length ? [{ index, text: tip.text.trim(), sticker, notices }] : [];
    });
}

/** 这条事实说的是哪个地点（subject：station_id 或地名，I 答对）；对不上就不写地名。 */
function placeNameOf(subject: string, ctx: Ctx): string | null {
  const stop = (ctx.revision?.stops ?? []).find((s) => (s.station_id != null && s.station_id === subject) || s.name === subject);
  return stop?.name ?? null;
}

/**
 * 这条资料说的是什么，按类别认形状（2026-09-25 定稿，见 generated 的 TravelFact.value 说明）：
 * - 地点身份 {lat, lng}：不显示原值（导航只用站点的 nav_url）；
 * - 天气 {suitable, summary}：显示 summary（如“多云”）；
 * - 门票、交通费 {amount, currency, estimated}：和“现实参考”同一个口径，只写已核实、三样认得出的；没核实的数即使带着也不写；
 * - 怎么去、开放时间、公告、看点、地标（以及不认识的类别）：一句中文，原样显示；
 * - 被判 rejected 的是 null；认不出的形状（对象、数组、数字……）一律不原样倒给玩家。
 */
function factValueText(fact: TravelFact, ctx: Ctx): string | null {
  if (fact.verdict === "rejected") return null;
  const { value } = fact;
  if (fact.category === "destination_identity") return null;
  if (isPriceFact(fact)) {
    const price = isVerified(fact) ? priceOf(value) : null;
    return price ? `约 ${amountText(price.amount)}${NBSP}${currencyText(price.currency)}${ctx.isDemo ? "（演示）" : ""}${price.estimated ? "，估算" : ""}` : null;
  }
  if (fact.category === "weather") {
    if (!value || typeof value !== "object" || Array.isArray(value)) return null;
    const { summary } = value as Record<string, unknown>;
    return typeof summary === "string" ? summary.trim() || null : null;
  }
  return typeof value === "string" ? value.trim() || null : null;
}

function sourceView(fact: TravelFact, ctx: Ctx): SourceView {
  const refs: SourceRefView[] = [];
  fact.source_ids.forEach((id, index) => {
    // 计划里的来源只含已核验事实引用到的（后端定）：别的事实引用的来源不在列表里，就不列，不说成“没有来源”。
    const src = ctx.sources.get(id);
    if (!src) return;
    const retrieved = formatMoment(src.retrieved_at, ctx.zone);
    const published = formatPublished(src.published_at, ctx.zone);
    refs.push({
      key: `${index}-${id}`,
      publisher: src.publisher?.trim() || hostOf(src.url) || "来源没有注明",
      href: safeHref(src.url),
      times: [retrieved ? `抓取于 ${retrieved}` : null, published ? `发布于 ${published}` : null].filter((t): t is string => Boolean(t)),
    });
  });
  // 已核对的事实却一条来源都对不上：照实说。
  if (refs.length === 0 && isVerified(fact)) refs.push({ key: "none", publisher: "来源没有注明", href: null, times: [] });
  const refRetrieved = refs.some((r) => r.times.some((t) => t.startsWith("抓取于")));
  const refPublished = refs.some((r) => r.times.some((t) => t.startsWith("发布于")));
  const observed = formatMoment(fact.observed_at, ctx.zone);
  const applies = rangeText(fact.valid_from, fact.valid_until, ctx.zone);
  // 事实自己也带抓取 / 发布时间；来源那行已经写了的不重复。
  const retrieved = refRetrieved ? null : formatMoment(fact.retrieved_at, ctx.zone);
  const published = refPublished ? null : formatPublished(fact.published_at, ctx.zone);
  const times = [
    observed ? `观测于 ${observed}` : null,
    applies ? `适用：${applies}` : null,
    retrieved ? `抓取于 ${retrieved}` : null,
    published ? `发布于 ${published}` : null,
  ].filter((t): t is string => Boolean(t));
  // 资料本身没写日期就照实说，不拿抓取时间冒充资料的年代。
  if (!observed && !applies && !refPublished && !published) times.push("资料本身没有注明日期");
  return {
    id: fact.fact_id,
    topic: categoryText(fact.category),
    detail: factValueText(fact, ctx),
    pendingText: pendingTextOf(fact, ctx),
    refs,
    times,
  };
}

/**
 * 事实的日期，只用这一条自己的时间字段（2026-09-25 定稿：价钱的日期取本条自己的时间，不拿来源的时间顶）：
 * 观测日 → 适用区间 → 发布日 → 抓取日。价钱常带适用区间，只写起始日会被读成“那天的价”。
 */
function factDate(fact: TravelFact, ctx: Ctx): string | null {
  const observed = formatDay(fact.observed_at, ctx.zone);
  if (observed) return observed;
  const window = rangeText(fact.valid_from, fact.valid_until, ctx.zone);
  if (window) return window;
  return formatDay(fact.published_at, ctx.zone) ?? formatDay(fact.retrieved_at, ctx.zone);
}

/**
 * 现实参考费用（挂在价钱类事实上，一个地点可以有多条），只给主人看，不换算成星币：
 * - 没核实（过期、说法不一、没核实、没采用）：写“还没核实”，即使带着数也不报——不拿没核实的数冒充现在的价；
 *   旁边一行能查时写“待核”，联网检索不能用时写“暂时核不了”（不许诺会核）；
 * - 核实了但金额认不出来（币种、金额不对，或没写明是不是估算），或资料没有任何日期：整条不报金额，写“金额说不准，先不写”；
 * - 都齐了：约多少、币种中文名、日期，估算的标“估算”。
 */
function realCostViews(ctx: Ctx): RealCostView[] {
  const demo = ctx.isDemo ? "（演示）" : "";
  const unchecked = ctx.research ? "待核" : "暂时核不了";
  return ctx.factList.filter(isPriceFact).map((f) => {
    const place = placeNameOf(f.subject, ctx);
    const label = place ? `${place}的${categoryText(f.category)}` : categoryText(f.category);
    const day = factDate(f, ctx);
    if (!isVerified(f)) return { key: f.fact_id, text: `${label}：还没核实${demo}`, meta: day ? `${day} · ${unchecked}` : unchecked };
    const price = priceOf(f.value);
    if (!price || !day) return { key: f.fact_id, text: `${label}：金额说不准，先不写${demo}`, meta: day ?? "资料没有注明日期" };
    return { key: f.fact_id, text: `${label}：约 ${amountText(price.amount)}${NBSP}${currencyText(price.currency)}${demo}`, meta: price.estimated ? `${day} · 估算` : day };
  });
}

/**
 * 出发前会再核一遍的资料（preconditions：出发时必须仍然有效的关键事实，fact_id）。
 * 出发那一刻 C 逐条核（web_journey/service.py _assert_preconditions_fresh）：过期了就不出发，重新查一遍。只在想去 / 可以出发时说。
 * 联网检索不能用时“重新查一遍”兑现不了（出发那一刻的核对只看有效期，照样会做）：说法见 PLAN_NOTES_NO_RESEARCH。
 */
function preconditionViews(ctx: Ctx): PreconditionView[] {
  if (ctx.stage !== "wish" && ctx.stage !== "ready") return [];
  return (ctx.revision?.preconditions ?? []).map((id, index) => {
    const fact = ctx.facts.get(id);
    if (!fact) return { key: `${index}-${id}`, topic: "其他资料", detail: null, pendingText: ctx.research ? "这条资料找不到了，待确认" : "这条资料找不到了", until: null };
    const until = isVerified(fact) ? formatUntilDay(fact.valid_until, ctx.zone) : null;
    return {
      key: `${index}-${id}`,
      topic: categoryText(fact.category),
      detail: factValueText(fact, ctx),
      pendingText: pendingTextOf(fact, ctx),
      until: until ? `有效到 ${until}` : null,
    };
  });
}

/**
 * 星币：出发前说攒钱目标（不是路费）；出发后只认这一版计划上的行程摘要（§30），绝不拿攒钱目标顶。
 * - fare 是这趟的标价，用没用券都不变；fare_waived 为真＝用了驾校借车券、实付 0：写“用了驾校借车券，这趟不用租车费”，
 *   标价单独给出、页面划掉显示（驾校触点：这张券唯一能被看见的地方）——标价绝不写成付过的钱；
 * - 判定顺序（契约）：先看 fare_sponsor，再看 fare_waived，最后看 fare。
 * - fare_sponsor 非空＝路费由平台补助（DS5，用户 2026-09-27「车费我们给补助即可，无需用户掏钱」）：不从 TA 的钱包扣、不核销借车券，
 *   写“这趟路费由星球活动补助”（活动以外的种类写“这趟路费由星球补助”）和“TA 没有花钱”；标价照写、划掉，不改数、不装成本来免费；
 *   补助来源的码（event: 这种）和活动号一律不显示；
 * - fare 为 0：这趟不花路费（带着券也一样，不划掉一个 0）；
 * - 形状不对（缺字段、fare 不是非负数、fare_sponsor 不是字符串或是空的）或还没关联行程：不显示路费。
 * 出发后才有这一行，包括出发后提前结束（取消但有行程）的回顾。攒钱目标只有心愿才有：只按计划本身说时不显示。
 */
/** 补助来源（`{kind}:{ref}`）只看种类说话：活动（event）写“星球活动补助”；不认识的种类也按补助说，不露码、不露活动号。 */
function sponsorText(sponsor: string): string {
  const kind = sponsor.trim().split(":")[0].trim().toLowerCase();
  return kind === "event" ? "这趟路费由星球活动补助" : "这趟路费由星球补助";
}

function coinView(journey: TravelJourneySummary | null, ctx: Ctx): CoinView | null {
  const { wish } = ctx;
  if (ctx.stage === "wish" || ctx.stage === "ready") {
    if (!wish) return null;
    const goal = wish.target_coins ?? wish.funds_goal;
    if (goal == null) return null;
    const text = `攒钱目标 ${coinsText(goal)}`;
    if (ctx.stage === "ready") return { text, detail: "已经攒够", listPrice: null };
    if (wish.waiting_reasons.includes("missing_funds") && wish.current_coins != null) {
      const at = formatMoment(wish.last_considered_at, ctx.zone);
      return { text, detail: `${at ? `截至 ${at} ` : ""}有 ${coinsText(wish.current_coins)}，还差 ${coinsText(Math.max(0, goal - wish.current_coins))}`, listPrice: null };
    }
    return { text, detail: null, listPrice: null };
  }
  if (afterDeparture(ctx.stage)) {
    if (!journey) return null;
    const { fare, fare_waived: waived, fare_sponsor: sponsor } = journey as Partial<TravelJourneySummary>;
    if (typeof fare !== "number" || !Number.isFinite(fare) || fare < 0 || typeof waived !== "boolean") return null;
    // 判定顺序（契约）：先看 fare_sponsor，再看 fare_waived，最后看 fare。
    if (sponsor != null) {
      // 形状不对（不是字符串、空的）：不猜是谁补的，也不当付过——这一行不显示。
      if (typeof sponsor !== "string" || !sponsor.trim()) return null;
      // 平台补助：TA 没有花钱。标价照写、划掉（它不是付过的钱）；补助来源的码和活动号不给玩家看。
      return { text: sponsorText(sponsor), detail: "TA 没有花钱", listPrice: fare > 0 ? coinsText(amountText(fare)) : null };
    }
    if (fare === 0) return { text: "这趟不花路费", detail: null, listPrice: null };
    // 用了驾校借车券：实付 0。fare 只作为划掉的标价出现，不是花出去的。
    if (waived) return { text: "用了驾校借车券，这趟不用租车费", detail: null, listPrice: coinsText(amountText(fare)) };
    return { text: `路费 ${coinsText(amountText(fare))}`, detail: "出发时从 TA 的星球银行卡付过", listPrice: null };
  }
  return null;
}

/** 这一版计划的手账：同一种页（plan / memory）取最新的一版（journal_revision 最大）。 */
function latestJournal(revision: TravelPlanRevision | null, phase: TravelJournal["phase"]): TravelJournal | null {
  let best: TravelJournal | null = null;
  for (const journal of revision?.journals ?? []) {
    if (journal.phase === phase && (!best || journal.journal_revision > best.journal_revision)) best = journal;
  }
  return best;
}

/** 手账图在画（processing：后端的 queued / running）时，计划多久只读重取一次。 */
export const JOURNAL_POLL_MS = 10_000;

/**
 * 计划页的手账图还在画时，多久只读重取一次计划（照证件照的做法：只发 GET，绝不替主人发起重画；2026-09-26 巡检 R14-A05）：
 * - 页面上显示的那一页手账（有回忆页显示回忆页，否则计划页；同一种取最新的一版）图在画（processing）→ 10 秒；
 * - 画好、没画成、结果没确认、被拒、没有图、没有手账、还没读到 → 不重取。
 * 点“重画这张手账”后返回的计划写回同一条查询（./JournalRedraw），图在画 → 接着每 10 秒重取，画好就停；
 * 离开页面时这条查询没人看了，react-query 自己停；页面切到后台也不查（refetchIntervalInBackground 默认关）。
 */
export function journalPollInterval(plan: TravelPlan | null | undefined): number | false {
  const revision = currentRevision(plan ?? null);
  const shown = latestJournal(revision, "memory") ?? latestJournal(revision, "plan");
  if (!shown || shown.image_refused?.trim()) return false;
  return shown.image_status === "processing" ? JOURNAL_POLL_MS : false;
}

/** 手账这一页上的文字：标题、一句话、站点（回忆页带章）、提醒、雨天备选。 */
function journalText(journal: TravelJournal, ctx: Ctx, who: string): JournalTextView {
  const memory = journal.phase === "memory";
  return {
    title: journal.title?.trim() || null,
    summary: journal.summary?.trim() || null,
    stations: journal.stations.map((s, index) => {
      let stamp: PlaceStamp | null = null;
      // 章只在回忆页、而且只在出发之后（和地点卡同一个口径）。
      if (memory && afterDeparture(ctx.stage)) {
        if (s.visited_event_ids.length > 0) stamp = { kind: "visited", label: "到过" };
        else if (ctx.stage === "back") stamp = { kind: "next", label: "下次" };
      }
      return { key: `${s.station_id ?? `#${index}`}:${s.name}`, name: s.name, role: s.role, stamp };
    }),
    tips: reminderViews(journal.owner_tips, ctx),
    noticeTips: noticeTipViews(journal.owner_tips, ctx, who),
    rain: journal.rain_alternative?.trim() || null,
  };
}

/**
 * 本地心愿（附近走走）按设计不画图的那个码：A 写在 image_refused 上（图字段都是 null，没有重画票；契约 TravelJournal.image_refused 的说明，
 * 用户 2026-09-27「不需要配」）。和“图没画成”、P 的拒绝码、没接插画（全 null 没有码的老数据）分开。
 */
export const LOCAL_TRIP_NO_IMAGE = "local_trip_no_image";

/** 这一页按设计不画图：只认 A 给的那个码，不去猜 null。 */
function textOnlyPage(journal: TravelJournal): boolean {
  return journal.image_refused?.trim() === LOCAL_TRIP_NO_IMAGE;
}

/**
 * 本地心愿（附近走走）的计划：这一版的 destination_key 以 local: 开头（I 定：不加新字段）。它的去处本来就不需要核实，
 * 地点卡不写“地点已核对 / 暂时核不了 / 待确认”那一行。
 */
export function isLocalPlan(revision: TravelPlanRevision): boolean {
  return revision.destination_key.startsWith("local:");
}

/** 手账这一页的读模型：画面与文字的说法见 journalCore；“星球活动：xxx”只照后端给的活动（空名字当没有）。 */
function journalView(journal: TravelJournal | null, planRevision: number | null, ctx: Ctx, who: string): JournalView {
  const title = journal?.planet_event?.title?.trim();
  const planetEvent = journal?.planet_event && title ? { event_id: journal.planet_event.event_id, title } : null;
  return { ...journalCore(journal, planRevision, ctx, who), planetEvent };
}

function journalCore(journal: TravelJournal | null, planRevision: number | null, ctx: Ctx, who: string): Omit<JournalView, "planetEvent"> {
  const none: Omit<JournalView, "planetEvent"> = { state: "none", heading: "手账", caption: "这一页只有文字，没有手账图", imageUrl: null, staleNote: null, canRedraw: false, redraw: null, identity: null, text: null, time: null };
  if (!journal) return none;
  const heading = journal.phase === "memory" ? "回忆手账" : "计划手账";
  const text = journalText(journal, ctx, who);
  // 这一页自己的时间（手账每一版一行，写下就不改；不拿计划的 created_at 顶）。
  const written = formatMoment(journal.created_at, ctx.zone);
  const time = written ? `这一页写于 ${written}` : null;
  // 重画入口只在 failed / unknown 且后端给了 redraw_ticket 时出现（有票才显示）。
  const ticket = Boolean(journal.redraw_ticket?.trim());
  const canRedraw = ticket && (journal.image_status === "failed" || journal.image_status === "unknown");
  const redraw = canRedraw ? { journalId: journal.journal_id, ticket: journal.redraw_ticket as string } : null;
  // 按设计不画图（本地心愿）：只有文字（标题、一句话、站点和章、提醒、雨天备选照常），没有图框，不说“只有文字 / 没有配图 / 没画成 / 还没确认”，
  // 不给重画。先于拒绝码判断——它写在 image_refused 上，但不是被拒。
  if (textOnlyPage(journal)) return { state: "text_only", heading, caption: "", imageUrl: null, staleNote: null, canRedraw: false, redraw: null, identity: null, text, time };
  const refused = journal.image_refused?.trim() || null;
  if (refused) {
    // 被拒：没有图，手账文字照样在；拒绝码不上页面（X-09）。
    return { state: "refused", heading, caption: "这次没有配图", imageUrl: null, staleNote: null, canRedraw, redraw, identity: null, text, time };
  }
  // §11.5：“没有手账图”用 image_status 缺省表达（没接插画），不是 failed。
  if (journal.image_status == null) return { ...none, heading, text, time };
  // 画里的 TA 从哪来：只在有图或正在画时说；“不画 TA”的原因码不上页面（X-09）。
  const identity = IDENTITY_TEXT[journal.identity_mode] ?? null;
  switch (journal.image_status) {
    case "ready": {
      const url = safeImageUrl(journal.image_url);
      if (!url) return { state: "unknown", heading, caption: "手账结果还没确认", imageUrl: null, staleNote: null, canRedraw, redraw, identity: null, text, time };
      // 不变量：journals 挂在这一版修订上，按理 plan_revision 一定相同；万一不同，说一句按上一版画的。
      const stale = planRevision != null && journal.plan_revision !== planRevision;
      return {
        state: "ready",
        heading,
        caption: "手账画面只画风景和心情；地名、时间、费用以这一页的文字为准",
        imageUrl: url,
        staleNote: stale ? "这张手账是按上一版计划画的，以这一页的文字为准" : null,
        canRedraw: false,
        redraw: null,
        identity,
        text,
        time,
      };
    }
    case "processing":
      return { state: "drawing", heading, caption: "画着呢", imageUrl: null, staleNote: null, canRedraw: false, redraw: null, identity, text, time };
    case "failed":
      return { state: "failed", heading, caption: "这次手账没画成", imageUrl: null, staleNote: null, canRedraw, redraw, identity: null, text, time };
    default:
      // unknown 与不认识的状态：只说“还没确认”，不说失败（可能已经画了并计费，§11.5）；不自动重画。
      return { state: "unknown", heading, caption: "手账结果还没确认", imageUrl: null, staleNote: null, canRedraw, redraw, identity: null, text, time };
  }
}

function titleOf(stage: PlanStage, who: string, destination: string): string {
  switch (stage) {
    case "wish":
      return `${who} 想去 ${destination}`;
    case "ready":
      return `${who} 准备去 ${destination}`;
    case "departed":
      return `${who} 出发去 ${destination} 了`;
    case "back":
      return `${who} 从 ${destination} 回来了`;
    case "cancelled":
      return `去 ${destination} 的心愿先放下了`;
    default:
      return `${who} 的旅行计划`;
  }
}

function outcomeLine(places: PlaceView[], hasMemory: boolean): string {
  if (!hasMemory) return "TA 回来了，到访记录还在整理";
  const visited = places.filter((p) => p.stamp?.kind === "visited").length;
  const next = places.filter((p) => p.stamp?.kind === "next").length;
  if (visited && next) return `去了 ${visited} 个地方，${next} 个留到下次`;
  if (visited) return `去了 ${visited} 个地方`;
  if (next) return `这趟没有到访记录，${next} 个地方留到下次`;
  return "这趟没有到访记录";
}

/**
 * 这条心愿已经写好（发布过）的计划：plan_id 和 plan_revision 都有才算，返回 plan_id；没有就是 null。
 * 后端在心愿一形成就给了 plan_id（app/web_travel/service.py 插入心愿行时写 tp-…），计划发布第一版时才写 plan_revision，
 * 后端自己判断“有没有计划”也看 plan_revision is not None。只看 plan_id 会把还在查资料、资料待补的心愿说成“计划已经写好了”，
 * 点进去却是 404 plan_not_found（DS5）。回忆页用的是同一口径（memories/travelMemory 的 publishedPlanId）。
 */
export function publishedPlanOf(wish: TravelWish | null | undefined): string | null {
  return wish?.plan_id && wish.plan_revision != null ? wish.plan_id : null;
}

/** 计划当前那一版（current_revision）；找不到就当没有计划全文。 */
function currentRevision(plan: TravelPlan | null): TravelPlanRevision | null {
  if (!plan) return null;
  return plan.revisions.find((r) => r.plan_revision === plan.current_revision) ?? null;
}

/**
 * 只按计划本身推状态（live 读到的心愿与这份计划对不上时）：这一版有回忆页＝已回来；关联了真实行程＝已出发；
 * 都没有＝状态待确认——不猜“想去 / 可以出发 / 已取消”，那些只有心愿才说得准。
 */
function stageOfPlan(revision: TravelPlanRevision | null): PlanStage {
  if (!revision) return "unknown";
  if (latestJournal(revision, "memory")) return "back";
  if (revision.journey) return "departed";
  return "unknown";
}

function build(wish: TravelWish | null, plan: TravelPlan | null, key: string, href: string, options: ViewOptions): PlanView {
  const revision = currentRevision(plan);
  const stage = wish ? (STAGE_OF[wish.status] ?? "unknown") : stageOfPlan(revision);
  const memoryJournal = latestJournal(revision, "memory");
  // 回来以后有回忆页就显示回忆页，否则显示计划页。
  const shownJournal = memoryJournal ?? latestJournal(revision, "plan");
  // 不变量：到访只认这一版计划的回忆页（journals 挂在修订上，plan_revision 按理一定相同）。
  const memory = memoryJournal && revision && memoryJournal.plan_revision === revision.plan_revision ? memoryJournal.stations : null;
  const ctx: Ctx = {
    stage,
    zone: zoneOf(options.timezone),
    isDemo: options.isDemo === true,
    wish,
    revision,
    facts: new Map((revision?.facts ?? []).map((f) => [f.fact_id, f])),
    factList: revision?.facts ?? [],
    sources: new Map((revision?.sources ?? []).map((s) => [s.source_id, s])),
    memory,
    // 只有读数据的一侧明说能力表里 travel.research 是 available 才算能查；没给就按不能查说（宁可不许诺）。
    research: options.researchAvailable === true,
  };
  const who = options.petName?.trim() || "TA";
  const published = publishedPlanOf(wish);

  // 只有“想去”（active）才列“还差什么”；排序后的第一条同时决定状态标签、列表卡片那句和地图那一行。
  const ordered =
    stage === "wish" && wish
      ? (wish.waiting_reasons ?? [])
          .map((code, index) => ({ code: String(code), index }))
          .sort((a, b) => (isReason(a.code) ? WAITING_ORDER[a.code] : 99) - (isReason(b.code) ? WAITING_ORDER[b.code] : 99) || a.index - b.index)
      : [];
  const waiting = wish ? ordered.map(({ code, index }) => waitingItem(code, index, wish, ctx)) : [];
  const firstCode = ordered[0]?.code;
  const chips = ctx.research ? WAITING_REASON_CHIPS : WAITING_REASON_CHIPS_NO_RESEARCH;
  const status = stage === "wish" ? (firstCode && isReason(firstCode) ? chips[firstCode] : { label: "还在准备", tone: "neutral" as ChipTone }) : STAGE_CHIP[stage];

  const stops = revision?.stops ?? [];
  const mainIndex = stops.findIndex((s) => s.role === "main");
  // 目的地：心愿写的名字；只按计划说时用这一版主目的地的站名（计划里恒有一个主目的地），再没有才用这一版计划的标题。
  const destination = wish?.destination_name?.trim() || (mainIndex >= 0 ? stops[mainIndex].name.trim() : "") || revision?.title?.trim() || "这个地方";
  const main: PlaceView =
    revision && mainIndex >= 0
      ? placeView(stops[mainIndex], mainIndex, revision, ctx)
      : { id: `${key}:destination`, role: "main", name: destination, why: null, tip: null, pending: false, verified: false, verifyText: unverifiedPlace(ctx), stamp: null, mapUrl: null };
  const suggestions = revision ? stops.flatMap((s, i) => (i !== mainIndex && s.role === "suggested" ? [placeView(s, i, revision, ctx)] : [])) : [];

  const outcome = outcomeLine([main, ...suggestions], Boolean(memory));
  const journeyId = revision?.journey?.journey_id ?? wish?.journey_id ?? null;
  const summary =
    stage === "wish"
      ? waiting.length
        ? `还差 ${waiting.length} 件事，准备好了再出发`
        : "还在准备，准备好了再出发"
      : stage === "ready"
        ? "该准备的都齐了"
        : stage === "departed"
          ? `${who} 已经在路上；回来以后，真的到过的地方才盖章`
          : stage === "back"
            ? outcome
            : stage === "cancelled"
              ? journeyId
                ? "这趟提前结束了，只给真的到过的地方盖章"
                : "这个心愿先放下了，没有出发"
              : "这份计划的状态还在确认";
  const cardLine =
    stage === "wish"
      ? (waiting[0]?.text ?? "还在准备")
      : stage === "ready"
        ? "都准备好了"
        : stage === "departed"
          ? "已经在路上"
          : stage === "back"
            ? outcome
            : stage === "cancelled"
              ? "这个心愿先放下了"
              : "状态还在确认";

  return {
    key,
    href,
    planRevision: revision?.plan_revision ?? null,
    isDemo: ctx.isDemo,
    stage,
    title: titleOf(stage, who, destination),
    destination,
    planTitle: revision?.title?.trim() || null,
    status,
    summary,
    cardLine,
    reason: wish?.owner_reason?.trim() || null,
    waiting,
    preconditions: preconditionViews(ctx),
    arrangement: revision?.summary?.trim() || null,
    planWritten: Boolean(revision),
    planLink: !revision && published ? `/guides/plan/${encodeURIComponent(published)}` : null,
    validity: revision ? rangeText(revision.valid_from, revision.valid_until, ctx.zone) : null,
    main,
    suggestions,
    reminders: reminderViews(revision?.owner_tips ?? [], ctx),
    noticeTips: noticeTipViews(revision?.owner_tips ?? [], ctx, who),
    rainPlan: revision?.rain_alternative?.trim() || null,
    coins: coinView(revision?.journey ?? null, ctx),
    realCosts: realCostViews(ctx),
    journal: journalView(shownJournal, revision?.plan_revision ?? null, ctx, who),
    sources: ctx.factList.map((fact) => sourceView(fact, ctx)),
    notes: ctx.research ? PLAN_NOTES : PLAN_NOTES_NO_RESEARCH,
  };
}

/**
 * 回来了、但手上没有那份计划（没读到、读不到、对不上）时，攻略页卡片和心愿页顶上那一句：不下结论，去计划里看。
 * 不说“到访记录还在整理”——计划页可能早就有回忆手账、盖好了“到过”（巡检 R15-P7-07 / 08，DS5）。
 */
export const BACK_SEE_PLAN = "这一趟去了哪些地方，在计划里看";
/** 不变量（正常到不了：出发要有写好的计划）：回来了却没有写好的计划，只说回来了，不指去计划。 */
export const BACK_NO_PLAN = "TA 回来了";

/**
 * 心愿这一侧要读哪份计划来说“这一趟的结果”：只有回来了（completed）且计划写好了（publishedPlanOf）才读，给 plan_id；别的阶段 null，不读。
 * 读的一侧（./PlanPage 的攻略页卡片、心愿页）用和计划页、回忆页同一个查询键（./data 的 planQueryKey），共用缓存。
 */
export function outcomePlanOf(wish: TravelWish): string | null {
  return STAGE_OF[wish.status] === "back" ? publishedPlanOf(wish) : null;
}

/**
 * GET /travel/wish → 页面 /guides/wish：当前活动心愿。TravelWish 只带 plan_id / plan_revision、不带计划全文；
 * 计划全文在 /guides/plan/:planId（GET /travel/plans/{plan_id}），这一页给一个去那里的链接。
 * 回来了时，“这一趟的结果”（攻略页卡片那句、心愿页顶上那句）只有那份计划的回忆手账说得准：给了那份计划（plan_id、wish_id 都对得上）
 * 就照计划页的口径说（同一个 viewOfPlan，一字不差）；没给就不下结论（BACK_SEE_PLAN）。别的阶段、别的块都不看 plan，照旧只按心愿说。
 */
export function viewOfWish(wish: TravelWish, options: ViewOptions = {}, plan: TravelPlan | null = null): PlanView {
  const view = build(wish, null, wish.wish_id, "/guides/wish", options);
  if (view.stage !== "back") return view;
  const planId = publishedPlanOf(wish);
  const same = plan && planId && plan.plan_id === planId && plan.wish_id === wish.wish_id ? plan : null;
  if (same) {
    const known = viewOfPlan({ wish, plan: same }, options);
    return { ...view, summary: known.summary, cardLine: known.cardLine };
  }
  const line = planId ? BACK_SEE_PLAN : BACK_NO_PLAN;
  return { ...view, summary: line, cardLine: line };
}

/** /guides/plan/:planId：某一份计划（显示 current_revision 那一版）。 */
export function viewOfPlan(bundle: PlanBundle, options: ViewOptions = {}): PlanView {
  return build(bundle.wish, bundle.plan, bundle.plan.plan_id, `/guides/plan/${encodeURIComponent(bundle.plan.plan_id)}`, options);
}

/**
 * live 的 /guides/plan/:planId，读到的当前心愿（GET /travel/wish）与这份计划的 wish_id 对不上（以前的心愿）：只按计划本身说。
 * 状态从计划推（见 stageOfPlan）；TA 的理由、还差什么、攒钱目标只有心愿才有，一律不显示、不编。
 */
export function viewOfPlanOnly(plan: TravelPlan, options: ViewOptions = {}): PlanView {
  return build(null, plan, plan.plan_id, `/guides/plan/${encodeURIComponent(plan.plan_id)}`, options);
}

/**
 * 这张卡背后有没有写好的计划：读到了计划全文（演示计划、计划页），或者心愿已经有写好的计划（planLink，见 publishedPlanOf）。
 * 计划发布时就写好了那一页计划手账，所以有写好的计划就有攻略手账——攻略页据此决定下面“去过的”空着时怎么说（回忆页也把它算一份）。
 */
export function hasWrittenPlan(view: PlanView): boolean {
  return view.planWritten || view.planLink !== null;
}

/** 地图主状态面板那一行（第②期）：只在想去 / 可以出发时出现，“想去 {目的地} · {还差什么第一条}”。 */
export function wishNoteLine(view: PlanView): string | null {
  if (view.stage === "wish") return `想去 ${view.destination} · ${view.waiting[0]?.text ?? "还在准备"}`;
  if (view.stage === "ready") return `想去 ${view.destination} · 都准备好了`;
  return null;
}
